"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.diffGraphs = exports.normalizeGraph = exports.sortObjectKeys = exports.isPlainObject = void 0;
const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
exports.isPlainObject = isPlainObject;
// Recursively sorts object keys so two structurally-equal configs stringify
// identically. Array order and multiplicity are preserved (significant); slugs
// and all values are left untouched.
const sortObjectKeys = (value) => {
    const sortValue = (entry) => {
        if (Array.isArray(entry)) {
            return entry.map(sortValue);
        }
        if ((0, exports.isPlainObject)(entry)) {
            return (0, exports.sortObjectKeys)(entry);
        }
        return entry;
    };
    return Object.fromEntries(Object.entries(value)
        .sort(([left], [right]) => {
        if (left === right)
            return 0;
        return left < right ? -1 : 1;
    })
        .map(([key, entry]) => [key, sortValue(entry)]));
};
exports.sortObjectKeys = sortObjectKeys;
/**
 * Comparison-only normalization: sorts configuration object keys so a
 * key-order-only difference doesn't read as a real change. Slugs, values, and
 * edges are deliberately left intact — a diff must faithfully record any
 * slug/content change.
 */
const normalizeGraph = (graph) => ({
    dependencies: graph.dependencies,
    artifacts: graph.artifacts.map((artifact) => ({
        ...artifact,
        configuration: (0, exports.sortObjectKeys)(artifact.configuration),
    })),
});
exports.normalizeGraph = normalizeGraph;
// First artifact per slug — duplicate slugs are possible in malformed graphs,
// and the diff has always compared the first occurrence.
const firstBySlug = (graph) => {
    const bySlug = new Map();
    graph.artifacts.forEach((artifact) => {
        if (!bySlug.has(artifact.artifactSlug)) {
            bySlug.set(artifact.artifactSlug, artifact);
        }
    });
    return bySlug;
};
/**
 * Deterministic textual diff between two graphs, keyed by artifact slug. Output
 * is fully sorted so two structurally-identical graphs always produce a
 * byte-identical diff regardless of artifact/dependency ordering. Callers pass
 * graphs already normalized with {@link normalizeGraph} so config comparison is
 * key-order-insensitive. Returns '' when the graphs match.
 */
const diffGraphs = ({ graph1, graph2, }) => {
    const lines = [];
    if (graph1.artifacts.length !== graph2.artifacts.length) {
        lines.push(`Artifact count: original=${graph1.artifacts.length}, roundtripped=${graph2.artifacts.length}`);
    }
    if (graph1.dependencies.length !== graph2.dependencies.length) {
        lines.push(`Dependency count: original=${graph1.dependencies.length}, roundtripped=${graph2.dependencies.length}`);
    }
    const bySlug1 = firstBySlug(graph1);
    const bySlug2 = firstBySlug(graph2);
    const missingFromRoundtrip = [...bySlug1.keys()]
        .filter((s) => !bySlug2.has(s))
        .sort();
    const extraInRoundtrip = [...bySlug2.keys()]
        .filter((s) => !bySlug1.has(s))
        .sort();
    if (missingFromRoundtrip.length > 0) {
        lines.push(`Artifacts missing after roundtrip: ${missingFromRoundtrip.join(', ')}`);
    }
    if (extraInRoundtrip.length > 0) {
        lines.push(`Extra artifacts after roundtrip: ${extraInRoundtrip.join(', ')}`);
    }
    // Compare artifact content for shared slugs (sorted for a stable diff).
    const sharedSlugs = [...bySlug1.keys()].filter((s) => bySlug2.has(s)).sort();
    sharedSlugs.forEach((slug) => {
        const orig = bySlug1.get(slug);
        const rt = bySlug2.get(slug);
        if (!orig || !rt) {
            return;
        }
        if (orig.type !== rt.type) {
            lines.push(`[${slug}] type: "${orig.type}" → "${rt.type}"`);
        }
        if (orig.creditFacilitySlug !== rt.creditFacilitySlug) {
            lines.push(`[${slug}] creditFacilitySlug: "${orig.creditFacilitySlug}" → "${rt.creditFacilitySlug}"`);
        }
        if (orig.loanProductSlug !== rt.loanProductSlug) {
            lines.push(`[${slug}] loanProductSlug: "${orig.loanProductSlug}" → "${rt.loanProductSlug}"`);
        }
        if (JSON.stringify(orig.configuration) !== JSON.stringify(rt.configuration)) {
            lines.push(`[${slug}] configuration:\n  original:     ${JSON.stringify(orig.configuration)}\n  roundtripped: ${JSON.stringify(rt.configuration)}`);
        }
    });
    // Compare dependencies as slug→slug pairs (sorted).
    const depStrings = (graph) => {
        const idToSlug = new Map(graph.artifacts.map((a) => [a.id, a.artifactSlug]));
        return new Set(graph.dependencies.map((dep) => `${idToSlug.get(dep.inputArtifactId) ?? dep.inputArtifactId} → ${idToSlug.get(dep.outputArtifactId) ?? dep.outputArtifactId}`));
    };
    const origDeps = depStrings(graph1);
    const rtDeps = depStrings(graph2);
    const missingDeps = [...origDeps].filter((d) => !rtDeps.has(d)).sort();
    const extraDeps = [...rtDeps].filter((d) => !origDeps.has(d)).sort();
    if (missingDeps.length > 0) {
        lines.push(`Dependencies missing after roundtrip:\n  ${missingDeps.join('\n  ')}`);
    }
    if (extraDeps.length > 0) {
        lines.push(`Extra dependencies after roundtrip:\n  ${extraDeps.join('\n  ')}`);
    }
    return lines.join('\n');
};
exports.diffGraphs = diffGraphs;
//# sourceMappingURL=graphDiff.js.map